import {
  type MenuItem,
  type RestaurantDetails,
  type RestaurantPromotion as RestaurantPromotionType,
} from "@/API";
import type { FC } from "react";
import Text from "../Text";
import { formatPromotionToText } from "@/lib/utils";
import { Tag } from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  restaurant: RestaurantDetails | null;
};

type SortedPromotionsWithItems = {
  promotion: RestaurantPromotionType;
  items: MenuItem[];
};

const comparePomotion = (
  promotionA: RestaurantPromotionType,
  promotionB: RestaurantPromotionType,
): boolean => {
  return (
    promotionA.type === promotionB.type &&
    promotionA.buyCount === promotionB.buyCount &&
    promotionA.discount === promotionB.discount &&
    promotionA.freeCount === promotionB.freeCount &&
    promotionA.maxDiscount === promotionB.maxDiscount &&
    promotionA.minValue === promotionB.minValue
  );
};

const RestaurantPromotion: FC<Props> = ({ restaurant }) => {
  const { t } = useTranslation();
  if (!restaurant) return null;

  const { menuItems, promotions } = restaurant;

  const promotionsWithItems = promotions.filter(
    (promo) => promo?.itemId !== null,
  );

  const promotionsWithoutItems = promotions.filter((promo) => !promo?.itemId);

  let sortedPromotionsWithItems: SortedPromotionsWithItems[] = [];

  promotionsWithItems.forEach((promotion) => {
    if (promotion) {
      const item = menuItems.find((item) => item?.id === promotion.itemId);

      if (item) {
        const promotionAlreadyExist = sortedPromotionsWithItems.findIndex(
          (it) => comparePomotion(it.promotion, promotion),
        );

        if (promotionAlreadyExist === -1) {
          sortedPromotionsWithItems.push({
            promotion: promotion,
            items: [item],
          });

          return;
        }

        if (promotionAlreadyExist !== -1) {
          sortedPromotionsWithItems[promotionAlreadyExist] = {
            promotion,
            items: [
              ...sortedPromotionsWithItems[promotionAlreadyExist].items,
              item,
            ],
          };
        }
      }
    }
  });

  return (
    <div className="@container">
      {promotionsWithItems.length === 0 &&
        promotionsWithoutItems.length === 0 && (
          <div className="bg-[white] w-full p-[40px] flex items-center justify-center rounded-[8px]">
            <div className="flex items-center flex-col gap-[10px]">
              <div className="flex items-center justify-center h-[32px] w-[32px] bg-[#F7F8F9] rounded-[50%]">
                <Tag height={16} width={16} color="#758195" />
              </div>
              <Text fontSize={14} fontWeight={400}>
                {t("restaurant_no_promotion")}
              </Text>
            </div>
          </div>
        )}

      {promotionsWithoutItems.length > 0 && (
        <>
          <Text fontWeight={600} fontSize={14}>
            {t("general")}
          </Text>
          <div className="grid @md:grid-cols-2 @xl:grid-cols-3 @6xl:grid-cols-4 @7xl:grid-cols-7 mt-[24px] mb-[24px]">
            {promotionsWithoutItems.map((promo) => {
              if (!promo) return null;
              return (
                <div
                  key={`${JSON.stringify(promo)}`}
                  className="bg-[white] p-[16px] rounded-[12px]"
                >
                  <div className="bg-[#F7F8F9] px-[8px] py-[4px] rounded-[40px] w-[fit-content]">
                    <Text fontSize={14} fontWeight={600}>
                      {formatPromotionToText(promo, t)}
                    </Text>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {sortedPromotionsWithItems.length > 0 && (
        <>
          <Text fontWeight={600} fontSize={14}>
            {t("products")}
          </Text>
          {sortedPromotionsWithItems.map(({ promotion, items }) => (
            <div key={`${JSON.stringify(promotion)}`} className="mt-[24px]">
              <Text fontWeight={600} fontSize={18}>
                {formatPromotionToText(promotion, t)}
              </Text>
              <div className="grid @md:grid-cols-2 @xl:grid-cols-3 @6xl:grid-cols-4 @7xl:grid-cols-7 gap-x-[16px] gap-y-[24px] mt-[8px]">
                {items.map((item) => (
                  <div
                    className="bg-[white] rounded-[12px] p-[16px]"
                    key={`${JSON.stringify(promotion)}-${item.id}`}
                  >
                    <img
                      src={item?.imageUrl ?? undefined}
                      alt={item.name}
                      className="mb-[12px] aspect-3/2 object-cover"
                    />
                    <div className="flex flex-col gap-[4px]">
                      <Text fontWeight={600} fontSize={14}>
                        {item.name}
                      </Text>
                      <Text fontWeight={400} fontSize={14}>
                        {`${(item.price / 100).toFixed(2)} ${restaurant.currencySymbol}`}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default RestaurantPromotion;
